import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {SeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {StudySearchItem} from 'studies/StudySearchInput';
import {ProcedureRunSelectionForAnalysis} from 'studies/typed/shared/useProcedureRunSelectionForAnalysis';
import {
  CREATE_ANALYSIS_NO_PERMISSION_MESSAGE,
  getInitialAnalysisNameForSelectedProcedureRuns,
  getInitialAnalysisNameForStudy,
  NO_PROCEDURE_RUNS_TO_ANALYZE_MESSAGE,
} from 'studies/typed/utils/analysis/analysisUtils';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import ProcedureBasedStudyAnalysisModalBanner from 'studies/typed/utils/analysis/ProcedureBasedStudyAnalysisModalBanner';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';

const STUDY_ARCHIVED_MESSAGE = 'Unarchive your study to generate an analysis';

const useStyles = createStyles((theme) => ({
  selectedRunsText: {
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.5,
    fontFamily: theme.fontFamily,
    color: theme.colors.gray[6],
    alignSelf: 'center',
  },
}));

/**
 * Props for AnalyzeStudyButton component.
 *
 * @package exported for testing.
 */
export interface AnalyzeStudyButtonProps {
  study: StudySearchItem;
  hasProcedureRuns: boolean;
  procedureType: ProcedureType;
  selectedProcedureRuns?: Array<ProcedureRunSelectionForAnalysis>;
  studyType: StudyType;
  isStudyArchived: boolean;
  actionOrigin: ActionOrigin;
}

export default function AnalyzeStudyButton(props: AnalyzeStudyButtonProps): JSX.Element {
  const {classes} = useStyles();
  let onClickHandler: () => void = noop;

  const {
    study,
    studyType,
    hasProcedureRuns,
    selectedProcedureRuns = [],
    procedureType,
    isStudyArchived,
    actionOrigin,
  } = props;
  const selectedProcedureRunIds = selectedProcedureRuns.map((procedureRun) => procedureRun.id);
  const isAnalyzingSelectedProcedureRuns = selectedProcedureRunIds.length > 0;
  const getInitialAnalysisName = (): string => {
    if (!isAnalyzingSelectedProcedureRuns) {
      return getInitialAnalysisNameForStudy(study.name);
    }
    return getInitialAnalysisNameForSelectedProcedureRuns(
      study.name,
      selectedProcedureRuns.map((procedureRun) => procedureRun.name)
    );
  };

  onClickHandler = async () => {
    const initialSeedDataConfig: SeedDataConfig = isAnalyzingSelectedProcedureRuns
      ? {
          type: SeedDataConfigType.PROCEDURE_RUN,
          data: {procedureRunIds: selectedProcedureRunIds},
        }
      : {
          type: SeedDataConfigType.STUDY,
          data: {studyIds: [study.id]},
        };

    await legacyRunModal(CreateAnalysisModal, {
      actionOrigin,
      onCreateAnalysis: (data: CreateAnalysisParams) =>
        createAnalysisForStudy(data, {
          actionOrigin,
          studyType,
          procedureType,
          sourceObjectIds: isAnalyzingSelectedProcedureRuns ? selectedProcedureRunIds : [study.id],
        }),
      initialStudySearchItems: [study],
      disableEditStudies: true,
      title: 'Generate analysis',
      initialAnalysisName: getInitialAnalysisName,
      headerInlineBanner: <ProcedureBasedStudyAnalysisModalBanner studyType={studyType} />,
      hideDataSharingWarningBanner: true,
      initialSeedDataConfig,
    });
  };

  let tooltipText: string | null = null;
  const buttonDisabledDueToArchivedStudy = isStudyArchived;
  const buttonDisabledDueToPermissions = !isFeatureEnabledForProcedureType(
    procedureType,
    ProceduresFeature.CREATE_ANALYSIS
  );
  const buttonDisabledDueToNoRuns = !hasProcedureRuns;
  const buttonDisabled =
    buttonDisabledDueToArchivedStudy || buttonDisabledDueToPermissions || buttonDisabledDueToNoRuns;
  if (buttonDisabledDueToArchivedStudy) {
    tooltipText = STUDY_ARCHIVED_MESSAGE;
  } else if (buttonDisabledDueToPermissions) {
    tooltipText = CREATE_ANALYSIS_NO_PERMISSION_MESSAGE;
  } else if (buttonDisabledDueToNoRuns) {
    tooltipText = NO_PROCEDURE_RUNS_TO_ANALYZE_MESSAGE;
  }

  return (
    <>
      {isAnalyzingSelectedProcedureRuns && (
        <span className={classes.selectedRunsText}>
          {selectedProcedureRunIds.length}{' '}
          {getProcedureRunUIStringForStudyType(studyType, {plural: selectedProcedureRunIds.length !== 1})}{' '}
          selected for analysis
        </span>
      )}
      <Tooltip text={tooltipText} isDisabled={!buttonDisabled || tooltipText == null}>
        <Button
          dataTestAttrs={{
            component: 'AnalyzeStudyButton',
            element: 'root',
          }}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          onClick={onClickHandler}
          text="Analyze"
          icon={ModelTypeConstants.ANALYSIS}
          disabled={buttonDisabled}
        />
      </Tooltip>
    </>
  );
}
